import _extends from "@babel/runtime/helpers/extends";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

/** @jsx jsx */
import { forwardRef, memo, useMemo, useState } from 'react';
import { jsx } from '@emotion/core';
import { usePlatformLeafEventHandler } from '@atlaskit/analytics-next';
import CloseIcon from '@atlaskit/icon/glyph/editor/close';
import CheckIcon from '@atlaskit/icon/glyph/editor/done';
import GlobalTheme from '@atlaskit/theme/components';
import { getStyles } from './internal/styles';
import { name as packageName, version as packageVersion } from './version.json';
export var getIconSize = function getIconSize(size) {
  return size === 'large' ? 'large' : 'small';
};

var noop = function noop() {};

var analyticsAttributes = {
  componentName: 'toggle',
  packageName: packageName,
  packageVersion: packageVersion
};
var InnerToggle = /*#__PURE__*/forwardRef(function (props, ref) {
  var _props$defaultChecked = props.defaultChecked,
      defaultChecked = _props$defaultChecked === void 0 ? false : _props$defaultChecked,
      _props$isDisabled = props.isDisabled,
      isDisabled = _props$isDisabled === void 0 ? false : _props$isDisabled,
      _props$onBlur = props.onBlur,
      providedOnBlur = _props$onBlur === void 0 ? noop : _props$onBlur,
      _props$onChange = props.onChange,
      providedChange = _props$onChange === void 0 ? noop : _props$onChange,
      _props$onFocus = props.onFocus,
      providedFocus = _props$onFocus === void 0 ? noop : _props$onFocus,
      _props$size = props.size,
      size = _props$size === void 0 ? 'regular' : _props$size,
      _props$name = props.name,
      name = _props$name === void 0 ? '' : _props$name,
      _props$value = props.value,
      value = _props$value === void 0 ? '' : _props$value,
      isChecked = props.isChecked,
      analyticsContext = props.analyticsContext,
      id = props.id,
      testId = props.testId,
      mode = props.mode;
  var isControlled = typeof isChecked === 'undefined';

  var _useState = useState(defaultChecked),
      _useState2 = _slicedToArray(_useState, 2),
      checked = _useState2[0],
      setChecked = _useState2[1];

  var handleBlur = usePlatformLeafEventHandler(_objectSpread({
    fn: providedOnBlur,
    action: 'blur',
    analyticsData: analyticsContext
  }, analyticsAttributes));
  var handleFocus = usePlatformLeafEventHandler(_objectSpread({
    fn: providedFocus,
    action: 'focus',
    analyticsData: analyticsContext
  }, analyticsAttributes));
  var handleChange = usePlatformLeafEventHandler(_objectSpread({
    fn: function fn(event, analyticsEvent) {
      if (isControlled) {
        setChecked(function (checked) {
          return !checked;
        });
      }

      return providedChange(event, analyticsEvent);
    },
    action: 'change',
    analyticsData: analyticsContext
  }, analyticsAttributes));
  var shouldChecked = isControlled ? checked : isChecked;
  var controlProps = {
    'data-checked': shouldChecked ? shouldChecked : undefined,
    'data-disabled': isDisabled ? isDisabled : undefined,
    'data-size': size,
    'data-testid': testId ? testId : undefined
  };
  var toggleStyles = useMemo(function () {
    return getStyles(size, mode);
  }, [size, mode]);
  return (// https://product-fabric.atlassian.net/browse/DST-1969
    // eslint-disable-next-line jsx-a11y/label-has-associated-control,jsx-a11y/label-has-for
    jsx("label", _extends({}, controlProps, {
      css: toggleStyles
    }), jsx("input", {
      ref: ref,
      checked: shouldChecked,
      disabled: isDisabled,
      id: id,
      name: name,
      onBlur: handleBlur,
      onChange: handleChange,
      onFocus: handleFocus,
      type: "checkbox",
      value: value,
      "data-testid": testId && "".concat(testId, "--input")
    }), jsx(CheckIcon, {
      label: "check",
      size: getIconSize(size)
    }), jsx(CloseIcon, {
      label: "cross",
      size: getIconSize(size)
    }))
  );
});
var Toggle = /*#__PURE__*/memo( /*#__PURE__*/forwardRef(function Toggle(props, ref) {
  return jsx(GlobalTheme.Consumer, null, function (tokens) {
    var mode = tokens.mode;
    return jsx(InnerToggle, _extends({}, props, {
      mode: mode,
      ref: ref
    }));
  });
}));
Toggle.displayName = 'Toggle';
export default Toggle;